import { useTranslation } from 'react-i18next';
import { useMemo } from 'react';

/**
 * Enterprise-grade hook for Authentication translations with type safety and error handling
 * Provides centralized access to all Authentication-related translations with performance optimization
 */
export function useAuthTranslations() {
  // Use the default translation namespace since auth.json is merged into translation
  const { t } = useTranslation('translation');

  // Memoize translation functions to prevent re-renders
  const translations = useMemo(() => {
    // Helper functions for common translation patterns with type safety
    const getCommonText = (key: string, params?: Record<string, any>): string => {
      return String(t(`auth.common.${key}`, params || {}));
    };

    const getFieldText = (key: string, params?: Record<string, any>): string => {
      return String(t(`auth.common.fields.${key}`, params || {}));
    };

    const getPlaceholderText = (key: string, params?: Record<string, any>): string => {
      return String(t(`auth.common.placeholders.${key}`, params || {}));
    };

    const getValidationText = (key: string, params?: Record<string, any>): string => {
      return String(t(`auth.common.validation.${key}`, params || {}));
    };

    const getErrorText = (key: string, params?: Record<string, any>): string => {
      return String(t(`auth.common.errors.${key}`, params || {}));
    };

    const getStatusText = (key: string, params?: Record<string, any>): string => {
      return String(t(`auth.common.status.${key}`, params || {}));
    };

    const getBrandingText = (key: string, params?: Record<string, any>): string => {
      return String(t(`auth.common.branding.${key}`, params || {}));
    };

    const getLoginText = (key: string, params?: Record<string, any>): string => {
      return String(t(`auth.login.${key}`, params || {}));
    };

    const getRegisterText = (key: string, params?: Record<string, any>): string => {
      return String(t(`auth.register.${key}`, params || {}));
    };

    const getSetPasswordText = (key: string, params?: Record<string, any>): string => {
      return String(t(`auth.setPassword.${key}`, params || {}));
    };

    const getOrgRegisterText = (key: string, params?: Record<string, any>): string => {
      return String(t(`auth.organizationRegister.${key}`, params || {}));
    };

    // Safe translation wrapper that always returns string
    const tSafe = (key: string, params?: Record<string, any>): string => {
      return String(t(key, params || {}));
    };

    return {
      // Direct translation function with type safety
      t: tSafe,

      // Category-specific helpers
      getCommonText,
      getFieldText,
      getPlaceholderText,
      getValidationText,
      getErrorText,
      getStatusText,
      getBrandingText,
      getLoginText,
      getRegisterText,
      getSetPasswordText,
      getOrgRegisterText,

      // Common fields and elements used across auth forms
      common: {
        branding: {
          title: getBrandingText('title'),
        },
        fields: {
          fullName: getFieldText('fullName'),
          firstName: getFieldText('firstName'),
          lastName: getFieldText('lastName'),
          emailAddress: getFieldText('emailAddress'),
          email: getFieldText('email'),
          password: getFieldText('password'),
          newPassword: getFieldText('newPassword'),
          confirmPassword: getFieldText('confirmPassword'),
        },
        placeholders: {
          fullName: getPlaceholderText('fullName'),
          firstName: getPlaceholderText('firstName'),
          lastName: getPlaceholderText('lastName'),
          email: getPlaceholderText('email'),
          password: getPlaceholderText('password'),
          createPassword: getPlaceholderText('createPassword'),
        },
        validation: {
          minimumCharacters: getValidationText('minimumCharacters'),
          fillAllFields: getValidationText('fillAllFields'),
          passwordsMatch: getValidationText('passwordsMatch'),
          enterFullName: getValidationText('enterFullName'),
          passwordMinLength: getValidationText('passwordMinLength'),
          passwordsDoNotMatch: getValidationText('passwordsDoNotMatch'),
          missingRequiredInfo: getValidationText('missingRequiredInfo'),
        },
        errors: {
          invalidInvitationLink: getErrorText('invalidInvitationLink'),
          unexpectedError: getErrorText('unexpectedError'),
          setupError: getErrorText('setupError'),
        },
        status: {
          loadingOrgDetails: getStatusText('loadingOrgDetails'),
          invitationError: getStatusText('invitationError'),
          goToLogin: getStatusText('goToLogin'),
          emailFromInvitation: getStatusText('emailFromInvitation'),
          alreadyHaveAccount: getStatusText('alreadyHaveAccount'),
          welcomeToOrg: (orgName: string) => getStatusText('welcomeToOrg', { orgName }),
        },
      },

      // Login page specific translations
      login: {
        title: getLoginText('title'),
        subtitle: getLoginText('subtitle'),
        buttons: {
          signIn: getLoginText('buttons.signIn'),
          signingIn: getLoginText('buttons.signingIn'),
        },
        links: {
          createAccount: getLoginText('links.createAccount'),
          forgotPassword: getLoginText('links.forgotPassword'),
        },
      },

      // Register page specific translations
      register: {
        title: getRegisterText('title'),
        subtitle: getRegisterText('subtitle'),
        subtitleLink: getRegisterText('subtitleLink'),
        buttons: {
          createAccount: getRegisterText('buttons.createAccount'),
          creatingAccount: getRegisterText('buttons.creatingAccount'),
        },
        leftPanel: {
          title: getRegisterText('leftPanel.title'),
          description: getRegisterText('leftPanel.description'),
        },
        legal: {
          agreementText: getRegisterText('legal.agreementText'),
          termsOfService: getRegisterText('legal.termsOfService'),
          and: getRegisterText('legal.and'),
          privacyPolicy: getRegisterText('legal.privacyPolicy'),
        },
      },

      // Set password page specific translations
      setPassword: {
        title: getSetPasswordText('title'),
        subtitle: getSetPasswordText('subtitle'),
        emailInfo: getSetPasswordText('emailInfo'),
        buttons: {
          completeSetup: getSetPasswordText('buttons.completeSetup'),
          settingUp: getSetPasswordText('buttons.settingUp'),
        },
        leftPanel: {
          title: getSetPasswordText('leftPanel.title'),
          description: getSetPasswordText('leftPanel.description'),
        },
        success: {
          setupComplete: getSetPasswordText('success.setupComplete'),
          setupCompleteLogin: getSetPasswordText('success.setupCompleteLogin'),
        },
        errors: {
          setupFailed: getSetPasswordText('errors.setupFailed'),
        },
      },

      // Organization register page specific translations
      organizationRegister: {
        title: getOrgRegisterText('title'),
        subtitle: getOrgRegisterText('subtitle'),
        buttons: {
          joinOrganization: getOrgRegisterText('buttons.joinOrganization'),
          joiningOrganization: getOrgRegisterText('buttons.joiningOrganization'),
        },
        leftPanel: {
          title: getOrgRegisterText('leftPanel.title'),
          description: getOrgRegisterText('leftPanel.description'),
        },
        invitation: {
          youveBeenInvited: getOrgRegisterText('invitation.youveBeenInvited'),
        },
      },
    };
  }, [t]);

  return translations;
}

export type AuthTranslations = ReturnType<typeof useAuthTranslations>;
